import { useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { mockEditions, mockLayoutPages } from '@/mocks/editions';
import { mockArticles } from '@/mocks/articles';

const statusStyles: Record<string, { border: string; text: string; label: string }> = {
  aprovado: { border: 'border-[#1a7a1a]', text: 'text-[#1a7a1a]', label: 'Aprovado' },
  revisão: { border: 'border-[#B8860B]', text: 'text-[#B8860B]', label: 'Revisão' },
  rascunho: { border: 'border-[#999999]', text: 'text-[#666666]', label: 'Rascunho' },
};

const sectionColors: Record<string, string> = {
  Política: 'text-[#C41230]',
  Esportes: 'text-[#1a7a1a]',
  Cultura: 'text-[#5B2D8E]',
  Economia: 'text-[#B8860B]',
  Ciência: 'text-[#0066AA]',
};

export default function LayoutEnginePage() {
  const [selectedEdition, setSelectedEdition] = useState(mockEditions[0].id);
  const [selectedPage, setSelectedPage] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [zoom, setZoom] = useState(100);

  const edition = mockEditions.find((e) => e.id === selectedEdition)!;
  const pages = mockLayoutPages.filter((p) => p.editionId === selectedEdition);
  const approvedArticles = mockArticles.filter((a) => a.status === 'aprovado');

  const handleGenerate = () => {
    setGenerating(true);
    setTimeout(() => setGenerating(false), 2500);
  };

  return (
    <AppLayout>
      <div className="p-6 space-y-5">

        {/* Controls Bar */}
        <div className="bg-white border border-[#E0E0E0] border-t-2 border-t-[#111111]">
          <div className="px-5 py-3 flex flex-wrap items-center gap-4">
            {/* Edition selector */}
            <div className="flex items-center gap-2">
              <label className="text-[10px] font-bold text-[#666666] uppercase tracking-wider font-sans whitespace-nowrap">Edição:</label>
              <select
                value={selectedEdition}
                onChange={(e) => setSelectedEdition(e.target.value)}
                className="px-3 py-1.5 text-xs border border-[#E0E0E0] focus:outline-none focus:border-[#111111] cursor-pointer font-sans bg-white"
              >
                {mockEditions.map((e) => (
                  <option key={e.id} value={e.id}>#{e.number} — {e.theme}</option>
                ))}
              </select>
            </div>

            {/* Zoom */}
            <div className="flex items-center gap-0 border border-[#E0E0E0]">
              {[75, 100, 125].map((z) => (
                <button
                  key={z}
                  onClick={() => setZoom(z)}
                  className={`px-3 py-1.5 text-[10px] font-bold font-sans cursor-pointer whitespace-nowrap transition-colors ${zoom === z ? 'bg-[#111111] text-white' : 'bg-white text-[#666666] hover:bg-[#F0F0F0]'}`}
                >
                  {z}%
                </button>
              ))}
            </div>

            {/* Status summary */}
            <div className="flex items-center gap-3">
              {[
                { label: 'Aprovadas', count: pages.filter((p) => p.status === 'aprovado').length, color: 'text-[#1a7a1a]' },
                { label: 'Em Revisão', count: pages.filter((p) => p.status === 'revisão').length, color: 'text-[#B8860B]' },
                { label: 'Rascunho', count: pages.filter((p) => p.status === 'rascunho').length, color: 'text-[#999999]' },
              ].map((s) => (
                <div key={s.label} className="flex items-center gap-1">
                  <span className={`text-xs font-bold font-serif ${s.color}`}>{s.count}</span>
                  <span className="text-[10px] text-[#999999] font-sans">{s.label}</span>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-0 ml-auto border border-[#E0E0E0]">
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="flex items-center gap-1.5 px-4 py-2 bg-white text-[#111111] text-[10px] font-bold font-sans uppercase tracking-wide hover:bg-[#F0F0F0] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 border-r border-[#E0E0E0]"
              >
                <i className={`${generating ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'} text-xs`}></i>
                {generating ? 'Gerando...' : 'Regenerar Layout'}
              </button>
              <button className="flex items-center gap-1.5 px-4 py-2 bg-[#111111] text-white text-[10px] font-bold font-sans uppercase tracking-wide hover:bg-[#333333] transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-file-pdf-line text-xs"></i>
                Exportar PDF
              </button>
            </div>
          </div>
        </div>

        {/* Main Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">

          {/* Pages Canvas — 3/4 */}
          <div className="lg:col-span-3">
            <div className="bg-white border border-[#E0E0E0]">
              <div className="px-5 py-3 border-b-2 border-[#111111] flex items-center justify-between">
                <h2 className="text-xs font-bold text-[#111111] font-serif uppercase tracking-wide">
                  Páginas da Edição #{edition.number}
                </h2>
                <span className="text-[10px] text-[#999999] font-sans">{pages.length} páginas geradas</span>
              </div>

              <div className="p-5">
                {pages.length === 0 ? (
                  <div className="text-center py-20 border border-dashed border-[#C8C8C8]">
                    <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 border border-[#E0E0E0]">
                      <i className="ri-layout-grid-line text-[#C8C8C8] text-xl"></i>
                    </div>
                    <p className="text-sm font-serif text-[#666666] mb-1">Nenhuma página gerada ainda</p>
                    <p className="text-xs text-[#999999] font-sans mb-4">Clique em "Regenerar Layout" para começar</p>
                    <button
                      onClick={handleGenerate}
                      className="px-6 py-2 bg-[#111111] text-white text-xs font-bold font-sans uppercase tracking-wide hover:bg-[#333333] cursor-pointer whitespace-nowrap transition-colors"
                    >
                      Gerar Layout Automático
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {pages.map((page) => {
                      const st = statusStyles[page.status] || statusStyles.rascunho;
                      const isSelected = selectedPage === page.id;
                      return (
                        <div
                          key={page.id}
                          onClick={() => setSelectedPage(isSelected ? null : page.id)}
                          className={`cursor-pointer border-2 transition-all overflow-hidden ${isSelected ? 'border-[#111111]' : 'border-[#E0E0E0] hover:border-[#999999]'}`}
                        >
                          {/* Page thumbnail */}
                          <div className="aspect-[3/4] relative overflow-hidden bg-[#F8F8F8]">
                            <img
                              src={`https://public.readdy.ai/ai/img_res/d7d2342b5848640c9357f0fd2aa9e58b.jpg$%7Bpage.type%7D%20black%20white&width=300&height=400&seq=pg${page.id}&orientation=portrait`}
                              alt={`Página ${page.number}`}
                              className="w-full h-full object-cover object-top"
                            />
                            {/* Overlay on selected */}
                            {isSelected && (
                              <div className="absolute inset-0 bg-[#111111]/10"></div>
                            )}
                            {/* Page number badge */}
                            <div className="absolute top-0 left-0 bg-[#111111] px-2 py-0.5">
                              <span className="text-[9px] font-bold text-white font-sans">{page.number}</span>
                            </div>
                            {/* Status badge */}
                            <div className="absolute top-0 right-0 bg-white border-l border-b border-[#E0E0E0] px-1.5 py-0.5">
                              <span className={`text-[9px] font-bold font-sans uppercase tracking-wide ${st.text}`}>{st.label}</span>
                            </div>
                          </div>
                          {/* Page info */}
                          <div className="px-2 py-1.5 bg-white border-t border-[#E0E0E0]">
                            <p className="text-[10px] font-bold text-[#111111] font-sans uppercase tracking-wide capitalize">{page.type}</p>
                            <p className="text-[9px] text-[#999999] font-sans">{page.articles} artigos</p>
                          </div>
                        </div>
                      );
                    })}

                    {/* Add page button */}
                    <div
                      onClick={handleGenerate}
                      className="cursor-pointer border-2 border-dashed border-[#C8C8C8] hover:border-[#111111] transition-colors flex flex-col items-center justify-center aspect-[3/4] bg-[#F8F8F8] hover:bg-[#F0F0F0]"
                    >
                      <i className="ri-add-line text-[#C8C8C8] text-xl mb-1"></i>
                      <span className="text-[9px] text-[#999999] font-sans uppercase tracking-wide">Nova Página</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Selected page detail */}
            {selectedPage && (
              <div className="mt-4 bg-white border border-[#E0E0E0] border-t-2 border-t-[#C41230]">
                <div className="px-5 py-3 border-b border-[#E0E0E0] flex items-center justify-between">
                  <h3 className="text-xs font-bold text-[#111111] font-serif uppercase tracking-wide">
                    Página {pages.find((p) => p.id === selectedPage)?.number} — Detalhes
                  </h3>
                  <div className="flex items-center gap-0 border border-[#E0E0E0]">
                    {['Aprovar', 'Editar', 'Remover'].map((action, i) => (
                      <button
                        key={action}
                        className={`px-3 py-1.5 text-[10px] font-bold font-sans uppercase tracking-wide cursor-pointer whitespace-nowrap transition-colors ${
                          i === 0 ? 'bg-[#1a7a1a] text-white hover:bg-[#155e15]' :
                          i === 1 ? 'bg-white text-[#444444] hover:bg-[#F0F0F0] border-x border-[#E0E0E0]' :
                          'bg-white text-[#C41230] hover:bg-[#FFF5F5]'
                        }`}
                      >
                        {action}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="p-4 grid grid-cols-3 gap-4">
                  {[
                    { label: 'Tipo', value: pages.find((p) => p.id === selectedPage)?.type || '—' },
                    { label: 'Artigos', value: pages.find((p) => p.id === selectedPage)?.articles || 0 },
                    { label: 'Status', value: pages.find((p) => p.id === selectedPage)?.status || '—' },
                  ].map((item) => (
                    <div key={item.label} className="border border-[#E0E0E0] p-3 text-center">
                      <p className="text-[9px] font-bold text-[#999999] uppercase tracking-wider font-sans mb-1">{item.label}</p>
                      <p className="text-sm font-bold text-[#111111] font-serif capitalize">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Sidebar — 1/4 */}
          <div className="space-y-4">

            {/* Edition Summary */}
            <div className="bg-white border border-[#E0E0E0] border-t-2 border-t-[#111111]">
              <div className="px-4 py-3 border-b border-[#E0E0E0]">
                <h3 className="text-[10px] font-bold text-[#111111] uppercase tracking-wider font-sans">Resumo da Edição</h3>
              </div>
              <div className="divide-y divide-[#F0F0F0]">
                {[
                  { label: 'Número', value: `#${edition.number}` },
                  { label: 'Data', value: edition.date },
                  { label: 'Tema', value: edition.theme },
                  { label: 'Páginas', value: edition.pages || pages.length },
                  { label: 'Artigos', value: edition.articles },
                  { label: 'Anúncios', value: edition.ads },
                ].map((item) => (
                  <div key={item.label} className="flex items-center justify-between px-4 py-2">
                    <span className="text-[10px] text-[#999999] font-sans">{item.label}</span>
                    <span className="text-[10px] font-bold text-[#111111] font-sans">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Available Articles */}
            <div className="bg-white border border-[#E0E0E0] border-t-2 border-t-[#C41230]">
              <div className="px-4 py-3 border-b border-[#E0E0E0] flex items-center justify-between">
                <h3 className="text-[10px] font-bold text-[#111111] uppercase tracking-wider font-sans">Artigos Aprovados</h3>
                <span className="text-[10px] font-bold text-[#C41230] font-sans">{approvedArticles.length}</span>
              </div>
              <div className="divide-y divide-[#F0F0F0]">
                {approvedArticles.map((a) => (
                  <div key={a.id} className="flex items-center gap-2.5 px-4 py-2.5 hover:bg-[#F8F8F8] cursor-pointer transition-colors">
                    <div className="w-8 h-8 overflow-hidden border border-[#E0E0E0] flex-shrink-0">
                      <img src={a.image} alt="" className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-bold text-[#111111] font-serif line-clamp-1">{a.title}</p>
                      <span className={`text-[9px] font-bold font-sans ${sectionColors[a.section] || 'text-[#666666]'}`}>{a.section}</span>
                    </div>
                    <button className="w-5 h-5 flex items-center justify-center flex-shrink-0 hover:bg-[#F0F0F0] transition-colors">
                      <i className="ri-add-line text-[#444444] text-xs"></i>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-[#111111] text-white border border-[#111111]">
              <div className="px-4 py-3 border-b border-white/10">
                <h3 className="text-[10px] font-bold uppercase tracking-wider font-sans">Ações Rápidas</h3>
              </div>
              <div className="p-3 space-y-2">
                {[
                  { icon: 'ri-magic-line', label: 'Auto-balancear páginas' },
                  { icon: 'ri-advertisement-line', label: 'Inserir anúncios' },
                  { icon: 'ri-check-double-line', label: 'Aprovar todas as páginas' },
                ].map((action) => (
                  <button
                    key={action.label}
                    className="w-full flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-white/10 text-white text-[10px] font-sans cursor-pointer transition-colors text-left whitespace-nowrap"
                  >
                    <i className={`${action.icon} text-xs flex-shrink-0`}></i>
                    <span>{action.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
